import { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Icon } from 'semantic-ui-react';
import PanelTransition from '../panel-transition/PanelTransition';
import './ResizableLayout.css';

const ResizableLayout = ({
  leftPanel,
  rightPanel,
  bottomPanel,
  panelPosition = 'right',
  isCollapsed = false,
  onResize,
  onPositionChange,
  onCollapseToggle,
  minPanelWidth = 250,
  maxPanelWidth = 600,
  minPanelHeight = 150,
  maxPanelHeight = 400,
  defaultPanelWidth = 300,
  defaultPanelHeight = 215,
  // Bulk edit props
  onBulkEditClick,
  isBulkSelectMode = false,
  hasClothingItems = true,
  // Selection props for header
  selectedCount = 0,
  totalCount = 0,
  onSelectAll,
  onUnselectAll,
  // Panel view props
  showPanelView = false,
  onPanelViewToggle,
  panelViewContent,
  // Closet panel state (for header controls in bottom panel)
  closetPanelState = null,
  // Lookbook panel state (for header controls in bottom panel)
  lookbookPanelState = null,
  // Panel view type: 'closet' or 'lookbook'
  panelViewType = 'closet',
  // Selected closet info
  // Selected closet info
  selectedClosetName = null,
  onBackToClosets,
  isLoggedIn = false,
  // New feature modal handlers
  onOpenEmailModal,
  onOpenExtensionModal
}) => {
  const [panelWidth, setPanelWidth] = useState(defaultPanelWidth);
  const [panelHeight, setPanelHeight] = useState(defaultPanelHeight);
  const [isDragging, setIsDragging] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionFrom, setTransitionFrom] = useState(null);
  const [isCollapsing, setIsCollapsing] = useState(false);

  // Use refs for touch event handling to avoid stale closures
  const isDraggingRef = useRef(false);
  const panelWidthRef = useRef(defaultPanelWidth);
  const panelHeightRef = useRef(defaultPanelHeight);

  const resizeHandleRef = useRef(null);
  const layoutRef = useRef(null);

  // Keep refs in sync with state
  useEffect(() => {
    isDraggingRef.current = isDragging;
  }, [isDragging]);

  useEffect(() => {
    panelWidthRef.current = panelWidth;
  }, [panelWidth]);

  useEffect(() => {
    panelHeightRef.current = panelHeight;
  }, [panelHeight]);

  // Load saved panel preferences from localStorage
  useEffect(() => {
    const savedPreferences = localStorage.getItem('panelLayoutPreferences');
    if (savedPreferences) {
      try {
        const preferences = JSON.parse(savedPreferences);
        if (preferences.panelWidth && preferences.panelWidth >= minPanelWidth && preferences.panelWidth <= maxPanelWidth) {
          setPanelWidth(preferences.panelWidth);
        }
        if (preferences.panelHeight && preferences.panelHeight >= minPanelHeight && preferences.panelHeight <= maxPanelHeight) {
          setPanelHeight(preferences.panelHeight);
        }
      } catch (error) {
        console.warn('Failed to load panel preferences:', error);
      }
    }
  }, [minPanelWidth, maxPanelWidth, minPanelHeight, maxPanelHeight]);

  // Save panel preferences to localStorage
  const savePreferences = useCallback(() => {
    const preferences = {
      panelWidth,
      panelHeight,
      panelPosition,
      isCollapsed
    };
    localStorage.setItem('panelLayoutPreferences', JSON.stringify(preferences));
  }, [panelWidth, panelHeight, panelPosition, isCollapsed]);

  // Save preferences when dimensions change
  useEffect(() => {
    savePreferences();
  }, [savePreferences]);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);

    document.body.style.cursor = panelPosition === 'right' ? 'ew-resize' : 'ns-resize';
    document.body.style.userSelect = 'none';
  }, [panelPosition]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;

    if (panelPosition === 'right') {
      // For right panel, calculate width based on distance from right edge
      const containerRect = layoutRef.current?.getBoundingClientRect();
      if (containerRect) {
        const newWidth = containerRect.right - e.clientX;
        const constrainedWidth = Math.max(minPanelWidth, Math.min(maxPanelWidth, newWidth));
        setPanelWidth(constrainedWidth);

        if (onResize) {
          onResize({ width: constrainedWidth, height: panelHeight, position: panelPosition });
        }
      }
    } else {
      // For bottom panel, calculate height based on distance from bottom edge
      const containerRect = layoutRef.current?.getBoundingClientRect();
      if (containerRect) {
        const newHeight = containerRect.bottom - e.clientY;
        const constrainedHeight = Math.max(minPanelHeight, Math.min(maxPanelHeight, newHeight));
        setPanelHeight(constrainedHeight);

        if (onResize) {
          onResize({ width: panelWidth, height: constrainedHeight, position: panelPosition });
        }
      }
    }
  }, [isDragging, panelPosition, minPanelWidth, maxPanelWidth, minPanelHeight, maxPanelHeight, panelWidth, panelHeight, onResize]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);

    // Remove global event listeners
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [handleMouseMove]);

  // Touch event handlers using refs to avoid stale closures
  const handleTouchStart = useCallback((e) => {
    // Prevent default to stop scrolling while dragging
    e.preventDefault();
    isDraggingRef.current = true;
    setIsDragging(true);
    document.body.style.userSelect = 'none';
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isDraggingRef.current) return;

    // Prevent scrolling while resizing
    if (e.cancelable) {
      e.preventDefault();
    }

    const touch = e.touches[0];

    if (panelPosition === 'right') {
      // For right panel, calculate width based on distance from right edge
      const containerRect = layoutRef.current?.getBoundingClientRect();
      if (containerRect) {
        const newWidth = containerRect.right - touch.clientX;
        const constrainedWidth = Math.max(minPanelWidth, Math.min(maxPanelWidth, newWidth));
        setPanelWidth(constrainedWidth);
        panelWidthRef.current = constrainedWidth;

        if (onResize) {
          onResize({ width: constrainedWidth, height: panelHeightRef.current, position: panelPosition });
        }
      }
    } else {
      // For bottom panel, calculate height based on distance from bottom edge
      const containerRect = layoutRef.current?.getBoundingClientRect();
      if (containerRect) {
        const newHeight = containerRect.bottom - touch.clientY;
        const constrainedHeight = Math.max(minPanelHeight, Math.min(maxPanelHeight, newHeight));
        setPanelHeight(constrainedHeight);
        panelHeightRef.current = constrainedHeight;

        if (onResize) {
          onResize({ width: panelWidthRef.current, height: constrainedHeight, position: panelPosition });
        }
      }
    }
  }, [panelPosition, minPanelWidth, maxPanelWidth, minPanelHeight, maxPanelHeight, onResize]);

  const handleTouchEnd = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
    document.body.style.userSelect = '';
  }, []);

  // Attach touch events with { passive: false } directly to the resize handle
  // This is required for mobile browsers that use passive touch events by default
  useEffect(() => {
    // Use RAF to ensure DOM is ready
    const rafId = requestAnimationFrame(() => {
      const resizeHandle = resizeHandleRef.current;
      if (!resizeHandle) return;

      // Attach touchstart with passive: false to allow preventDefault
      resizeHandle.addEventListener('touchstart', handleTouchStart, { passive: false });
    });

    return () => {
      cancelAnimationFrame(rafId);
      const resizeHandle = resizeHandleRef.current;
      if (resizeHandle) {
        resizeHandle.removeEventListener('touchstart', handleTouchStart);
      }
    };
  }, [handleTouchStart, isCollapsed, hasClothingItems]);

  // Handle global mouse and touch events when dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);

      if (!isDragging) {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Listen for highlightClothingPanel custom event
  useEffect(() => {
    const handleHighlight = () => {
      const container = layoutRef.current?.querySelector('.clothing-cards-container');
      if (container) {
        container.classList.remove('highlight-border');
        void container.offsetWidth;
        container.classList.add('highlight-border');
        setTimeout(() => container.classList.remove('highlight-border'), 1000);
      }
    };
    window.addEventListener('highlightClothingPanel', handleHighlight);
    return () => window.removeEventListener('highlightClothingPanel', handleHighlight);
  }, []);

  const handlePositionToggle = () => {
    const newPosition = panelPosition === 'right' ? 'bottom' : 'right';

    // Start transition animation
    setIsTransitioning(true);
    setTransitionFrom(panelPosition);

    // Change position after a brief delay to allow transition to start
    setTimeout(() => {
      if (onPositionChange) {
        onPositionChange(newPosition);
      }
    }, 50);

    // End transition after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
      setTransitionFrom(null);
    }, 350);
  };

  const handleCollapseToggle = () => {
    setIsCollapsing(true);

    // Add a small delay for smooth animation
    setTimeout(() => {
      if (onCollapseToggle) {
        onCollapseToggle(!isCollapsed);
      }
    }, 50);

    // End collapsing animation
    setTimeout(() => {
      setIsCollapsing(false);
    }, 350);
  };

  const getMainContentStyle = () => {
    if (isCollapsed) {
      return {
        [panelPosition === 'right' ? 'right' : 'bottom']: '0px'
      };
    }

    return {
      [panelPosition === 'right' ? 'right' : 'bottom']:
        `${panelPosition === 'right' ? panelWidth : panelHeight}px`
    };
  };

  const getPanelStyle = () => {
    return {
      [panelPosition === 'right' ? 'width' : 'height']:
        `${panelPosition === 'right' ? panelWidth : panelHeight}px`
    };
  };

  const getResizeHandleStyle = () => {
    if (panelPosition === 'right') {
      return {
        right: `${panelWidth}px`,
        transform: 'translateX(50%)',
        top: 0,
        bottom: 0,
        width: '4px',
        cursor: 'ew-resize'
      };
    } else {
      return {
        bottom: `${panelHeight}px`,
        transform: 'translateY(50%)',
        left: 0,
        right: 0,
        height: '4px',
        cursor: 'ns-resize'
      };
    }
  };

  return (
    <div
      ref={layoutRef}
      className={`resizable-layout ${panelPosition}-layout ${isDragging ? 'dragging' : ''} ${isCollapsing ? 'collapsing' : ''} ${isCollapsed ? 'collapsed' : 'expanded'} ${!hasClothingItems ? 'empty-panel' : ''}`}
    >
      {/* Main content area */}
      <div
        className="main-content"
        style={getMainContentStyle()}
      >
        {leftPanel}
      </div>

      {/* Panel and resize handle (only show when not collapsed) */}
      {!isCollapsed && (
        <>
          {/* Resize handle */}
          <div
            ref={resizeHandleRef}
            className={`resize-handle ${panelPosition}-handle ${isDragging ? 'active' : ''}`}
            style={getResizeHandleStyle()}
            onMouseDown={handleMouseDown}
          >
            <div className="resize-handle-indicator" />
          </div>

          {/* Side panel */}
          <div
            className={`side-panel ${panelPosition}-panel`}
            style={getPanelStyle()}
          >
            <PanelHeader
              onPositionChange={handlePositionToggle}
              onCollapseToggle={handleCollapseToggle}
              currentPosition={panelPosition}
              isTransitioning={isTransitioning}
              isCollapsing={isCollapsing}
              onBulkEditClick={onBulkEditClick}
              isBulkSelectMode={isBulkSelectMode}
              hasClothingItems={hasClothingItems}
              selectedCount={selectedCount}
              totalCount={totalCount}
              onSelectAll={onSelectAll}
              onUnselectAll={onUnselectAll}
              showPanelView={showPanelView}
              onPanelViewToggle={onPanelViewToggle}
              closetPanelState={closetPanelState}
              selectedClosetName={selectedClosetName}
              onBackToClosets={onBackToClosets}
              panelWidth={panelWidth}
              isLoggedIn={isLoggedIn}
              lookbookPanelState={lookbookPanelState}
              panelViewType={panelViewType}
              onOpenEmailModal={onOpenEmailModal}
              onOpenExtensionModal={onOpenExtensionModal}
            />
            <div className="clothing-panel-content">
              <PanelTransition
                isTransitioning={isTransitioning}
                fromPosition={transitionFrom}
                toPosition={panelPosition}
              >
                {showPanelView && panelViewContent ? panelViewContent : (panelPosition === 'right' ? rightPanel : bottomPanel)}
              </PanelTransition>
            </div>
          </div>
        </>
      )}

      {/* Collapsed panel handle - clickable bar with pull icon */}
      {isCollapsed && (
        <div
          className={`collapsed-bar ${panelPosition}-collapsed`}
          onClick={handleCollapseToggle}
          title="Expand panel"
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleCollapseToggle()}
        >
          <Icon name={panelPosition === 'right' ? 'caret left' : 'caret up'} />
        </div>
      )}
    </div>
  );
};

// Panel Header Component
const PanelHeader = ({
  onPositionChange,
  onCollapseToggle,
  currentPosition,
  isTransitioning = false,
  isCollapsing = false,
  onBulkEditClick,
  isBulkSelectMode = false,
  hasClothingItems = true,
  selectedCount = 0,
  totalCount = 0,
  onSelectAll,
  onUnselectAll,
  showPanelView = false,
  onPanelViewToggle,
  closetPanelState = null,
  selectedClosetName = null,
  onBackToClosets,
  panelWidth = 300,
  isLoggedIn = false,
  lookbookPanelState = null,
  panelViewType = 'closet',
  onOpenEmailModal,
  onOpenExtensionModal
}) => {
  const allSelected = totalCount > 0 && selectedCount === totalCount;
  const isBottomPanel = currentPosition === 'bottom';
  const isLookbookView = panelViewType === 'lookbook';
  const isLookbookBulkMode = isBottomPanel && lookbookPanelState?.isBulkMode;

  // Closet panel controls - only show in header when in bottom panel view and NOT in lookbook view
  const renderClosetControls = () => {
    // Only show closet controls in header when panel is at bottom and not in lookbook mode
    if (!isBottomPanel || !showPanelView || isLookbookView || !closetPanelState) return null;

    const { isBulkMode, selectedCount: closetSelectedCount, closetCount, allSelected: closetAllSelected,
      onNewCloset, onBulkEdit, onSelectAll: onClosetSelectAll, onDelete, onDone } = closetPanelState;

    if (isBulkMode) {
      return (
        <>
          <Button basic color="teal" size="tiny" onClick={onClosetSelectAll}>
            {closetAllSelected ? 'Unselect' : 'Select All'}
          </Button>
          <Button color="red" size="tiny" disabled={closetSelectedCount === 0} onClick={onDelete}>
            Delete ({closetSelectedCount})
          </Button>
          <Button color="teal" size="tiny" onClick={onDone}>
            Done
          </Button>
        </>
      );
    }

    return (
      <>
        <Button basic color="teal" size="tiny" onClick={onNewCloset}>
          + New Closet
        </Button>
        {closetCount > 1 && (
          <span
            onClick={onBulkEdit}
            style={{
              color: '#00b5ad',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              padding: '2px 4px',
              userSelect: 'none'
            }}
          >
            Select
          </span>
        )}
      </>
    );
  };

  // Lookbook panel controls - only show in header when in bottom panel view
  const renderLookbookControls = () => {
    // Only show lookbook controls in header when panel is at bottom
    if (!isBottomPanel || !showPanelView || !isLookbookView || !lookbookPanelState) return null;

    const { isBulkMode, selectedCount: lookbookSelectedCount, totalCount: lookbookTotalCount,
      allSelected: lookbookAllSelected, viewMode, onNewLookbook, onBulkEdit, onSelectAll, onDelete, onDone } = lookbookPanelState;

    if (isBulkMode) {
      return (
        <>
          <Button basic color="teal" size="tiny" onClick={onSelectAll}>
            {lookbookAllSelected ? 'Unselect' : 'Select All'}
          </Button>
          <Button color="teal" size="tiny" onClick={onDone}>
            Done
          </Button>
        </>
      );
    }

    return (
      <>
        {viewMode === 'list' && (
          <Button basic color="teal" size="tiny" onClick={onNewLookbook} icon>
            <Icon name="plus" /> New
          </Button>
        )}
        {lookbookTotalCount > 0 && (
          <span
            onClick={onBulkEdit}
            style={{
              color: '#00b5ad',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              padding: '2px 4px',
              userSelect: 'none'
            }}
          >
            Select
          </span>
        )}
      </>
    );
  };

  // Check if closet bulk mode is active (only relevant for bottom panel)
  const isClosetBulkMode = isBottomPanel && closetPanelState?.isBulkMode;

  // Calculate max characters based on panel width (responsive truncation)
  const getMaxCharacters = () => {
    // For right panel, calculate based on width
    // With more header space (no bulk edit button), we can show more characters
    // Approximate: 280px = 10 chars, 400px = 18 chars, 600px = 30 chars
    // Using linear interpolation
    const minWidth = 280;
    const maxWidth = 600;
    const minChars = 10;
    const maxChars = 30;

    const clampedWidth = Math.max(minWidth, Math.min(maxWidth, panelWidth));
    const ratio = (clampedWidth - minWidth) / (maxWidth - minWidth);
    return Math.floor(minChars + ratio * (maxChars - minChars));
  };

  // Truncate closet name responsively based on panel width
  const getTruncatedClosetName = (name) => {
    if (!name) return 'My Closet';
    if (!isBottomPanel) {
      const maxChars = getMaxCharacters();
      if (name.length > maxChars) {
        return name.substring(0, maxChars) + '…';
      }
    }
    return name;
  };

  return (
    <div className="panel-header">
      {/* Left side - All Closets navigation or Edit Mode text */}
      <div className="panel-nav">
        {isBulkSelectMode ? (
          <span className="edit-mode-text">Edit Mode</span>
        ) : showPanelView ? (
          isClosetBulkMode || isLookbookBulkMode ? (
            <span className="edit-mode-text">Edit Mode</span>
          ) : isLookbookView ? (
            <span className="closets-title">ALL LOOKBOOKS</span>
          ) : panelViewType === 'explore' ? (
            <span className="closets-title">COMMUNITY</span>
          ) : panelViewType === 'earnings' ? (
            <span className="closets-title">MY EARNINGS</span>
          ) : (
            <span className="closets-title">ALL CLOSETS</span>
          )
        ) : (
          isLoggedIn ? (
            <button
              className="all-closets-btn"
              title={selectedClosetName || 'My Closet'}
              onClick={() => onBackToClosets && onBackToClosets()}
            >
              <Icon name="chevron left" />
              <span className="nav-text">{getTruncatedClosetName(selectedClosetName)}</span>
            </button>
          ) : (
            <div className="panel-title-static">
              <span className="nav-text">My Closet</span>
            </div>
          )
        )}
      </div>

      {/* Right side controls */}
      <div className="panel-controls">
        {/* Closet panel controls when in panel view (bottom panel only) */}
        {renderClosetControls()}
        {/* Lookbook panel controls when in panel view (bottom panel only) */}
        {renderLookbookControls()}

        {/* Select text link - show in header for both panel positions */}
        {hasClothingItems && !isBulkSelectMode && !showPanelView && (
          <span
            onClick={onBulkEditClick}
            style={{
              color: '#00b5ad',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              padding: '2px 4px',
              userSelect: 'none'
            }}
          >
            Select
          </span>
        )}
        {/* Action icon buttons - removed as per request */}
        {/* Select All / Unselect All button when in bulk edit mode */}
        {isBulkSelectMode && (
          <Button
            basic
            color="teal"
            size="tiny"
            onClick={allSelected ? onUnselectAll : onSelectAll}
            className="bulk-edit-semantic-btn"
          >
            {allSelected ? 'Unselect' : 'Select All'}
          </Button>
        )}
        {/* Done button when in bulk edit mode */}
        {isBulkSelectMode && (
          <Button
            color="teal"
            size="tiny"
            onClick={onBulkEditClick}
            className="bulk-edit-semantic-btn"
          >
            Done
          </Button>
        )}
        {/* Layout position toggle - hidden on mobile, hidden in bulk mode */}
        {!isBulkSelectMode && !isClosetBulkMode && !isLookbookBulkMode && (
          <button
            onClick={onPositionChange}
            className={`position-toggle ${isTransitioning ? 'transitioning' : ''} hide-on-mobile`}
            title={`Move to ${currentPosition === 'right' ? 'bottom' : 'right'}`}
            disabled={isTransitioning}
          >
            <Icon name="map pin" size="small" />
          </button>
        )}
        {/* Collapse toggle - hidden in bulk mode */}
        {!isBulkSelectMode && !isClosetBulkMode && !isLookbookBulkMode && (
          <button
            onClick={onCollapseToggle}
            className={`collapse-toggle ${isCollapsing ? 'collapsing' : ''}`}
            title="Collapse panel"
            disabled={isCollapsing}
          >
            <Icon name="minus" size="small" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ResizableLayout;